import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  NgZone,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { BehaviorSubject, Subject, timer } from 'rxjs';
import { filter, map, take, takeUntil } from 'rxjs/operators';
import { environment } from '../../../environments';
import {
  ITrackingAction,
  ITrackingObject,
  ITrackingProperties,
} from 'i2g-ng-tracking';
import { WindowRefService } from 'i2g-ng-utils';
import { IconSizesEnum, IIcon } from 'i2g-ng-ui';
import { from } from 'rxjs';
import { IAppleIdData } from '../../account.data';

export interface IOauthAppleInfo {
  email: string;
  idToken: string;
}

@Component({
  selector: 'app-oauth-apple-circle',
  templateUrl: 'oauth-apple-circle.component.html',
  styleUrls: ['oauth-apple-circle.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OauthAppleCircleComponent implements OnInit, OnDestroy {
  @Input() trackingObject: ITrackingObject;
  @Input() trackingAction: ITrackingAction;
  @Input() trackingAdditionalProperties: ITrackingProperties;

  @Output() authenticationSuccess = new EventEmitter<IOauthAppleInfo>();
  @Output() authenticationError = new EventEmitter<Error>();

  appleIcon: IIcon;
  initialized$ = new BehaviorSubject(false);
  processing$ = new BehaviorSubject(false);

  private _destroyed: Subject<boolean> = new Subject<boolean>();

  constructor(
    private _windowRefService: WindowRefService,
    private _ngZone: NgZone
  ) {}

  ngOnInit() {
    this.appleIcon = {
      name: 'apple',
      size: IconSizesEnum.custom,
    };

    const win = this._windowRefService.nativeWindow;

    timer(0, 1000)
      .pipe(
        takeUntil(this._destroyed),
        map(() => win.AppleID),
        filter((x) => !!x),
        take(1)
      )
      .subscribe(() => {
        this._ngZone.run(() => {
          // The redirect uri should be on the same domain has the uri that open the Apple Sign In pop-up.
          // In this case, it will be account URL. Using location.origin to support all account web environments.
          // @see
          // https://developer.apple.com/documentation/sign_in_with_apple/sign_in_with_apple_js/configuring_your_webpage_for_sign_in_with_apple
          const redirectURI = location.origin;

          this._AppleID.auth.init({
            clientId: environment.appleClientId,
            scope: 'name email',
            redirectURI,
            state: '',
            usePopup: true,
          });

          this.initialized$.next(true);
        });
      });
  }

  signInToApple() {
    this.processing$.next(true);

    from(this._AppleID.auth.signIn())
      .pipe(takeUntil(this._destroyed))
      .subscribe(
        (data: IAppleIdData) => {
          this._ngZone.run(() => {
            const { authorization, user } = data;
            const email = user ? user.email : '';

            this.authenticationSuccess.emit({
              email,
              idToken: authorization.id_token,
            });
            this.processing$.next(false);
          });
        },
        (error) => {
          this._ngZone.run(() => {
            this.authenticationError.emit(error);
            this.processing$.next(false);
          });
        }
      );
  }

  ngOnDestroy() {
    this._destroyed.next(true);
    this._destroyed.unsubscribe();
  }

  get _AppleID() {
    return this._windowRefService.nativeWindow.AppleID;
  }
}
